import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { FileText } from 'lucide-react';

interface AnalyticsEvent {
  event_type: string;
  session_id: string;
  event_data: Record<string, unknown>;
}

interface TopPagesTableProps {
  events: AnalyticsEvent[];
}

const TopPagesTable = ({ events }: TopPagesTableProps) => {
  // Build session → page mapping from page_view events
  const sessionPages = new Map<string, string>();
  events.forEach(e => {
    if (e.event_type === 'page_view' && e.event_data?.page) {
      sessionPages.set(e.session_id, String(e.event_data.page));
    }
  });

  // Build page → metrics
  const pageMetrics = new Map<string, { views: number; uploads: number; compressions: number; downloads: number }>();

  events.forEach(e => {
    const page = sessionPages.get(e.session_id);
    if (!page) return;

    if (!pageMetrics.has(page)) {
      pageMetrics.set(page, { views: 0, uploads: 0, compressions: 0, downloads: 0 });
    }
    const m = pageMetrics.get(page)!;

    if (e.event_type === 'page_view') m.views++;
    else if (e.event_type === 'upload_success') m.uploads++;
    else if (e.event_type === 'compression_success') m.compressions++;
    else if (e.event_type === 'download_click') m.downloads++;
  });

  const rows = Array.from(pageMetrics.entries())
    .map(([page, m]) => ({ page, ...m }))
    .sort((a, b) => b.views - a.views)
    .slice(0, 10);

  if (rows.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Top Páginas
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Página</TableHead>
              <TableHead className="text-right">Views</TableHead>
              <TableHead className="text-right">Uploads</TableHead>
              <TableHead className="text-right">Compresiones</TableHead>
              <TableHead className="text-right">Descargas</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(r => (
              <TableRow key={r.page}>
                <TableCell className="max-w-[200px] truncate text-sm">{r.page}</TableCell>
                <TableCell className="text-right">{r.views}</TableCell>
                <TableCell className="text-right">{r.uploads}</TableCell>
                <TableCell className="text-right">{r.compressions}</TableCell>
                <TableCell className="text-right">{r.downloads}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default TopPagesTable;
